import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IFormObject, IService, } from 'sierra'
import Container from '../../../components/Container'
import { Button, CircularProgress, Divider, Grid2, IconButton, TextField, Typography, useTheme } from '@mui/material'
import { useApp } from '../../../contexts/AppContext'
import api from '../../../utils/axios'
import OrderStepper from '../../../components/OrderStepper'
import { ArrowBackIos, ArrowForwardIos, Close } from '@mui/icons-material'

const OrderCreate = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { lang, translate } = useApp()
    const theme = useTheme()

    if (!location.state?.service) navigate('/services')
    const service: IService = useMemo(() => location.state.service, [location])

    if (!service.steps) service.steps = []

    const [form, setForm] = useState<IFormObject[]>(
        service.service_version === JSON.parse(localStorage.getItem(`service_${service.service_id}`) || '""').version ?
            JSON.parse(localStorage.getItem(`service_${service.service_id}`) || '""').form :
            service.steps?.map(step => ({ step_id: step.step_id, step_name: step.step_name, fields: step.fields || [] })) || []
    )
    const [headsAmount, setHeadsAmount] = useState<number>(1)
    const [apiCall, setApiCall] = useState<boolean>(false)
    const [openConfirm, setOpenConfirm] = useState<boolean>(false)

    const completedSteps = useMemo(() =>
        form.map(s => (
            s.fields
                && s.fields[0]
                && s.fields.every(f =>
                    f.field_required === false ? true :
                        !f.field_value ? false :
                            f.field_type === 'multiselect' && Array.isArray(f.field_value) && f.field_value.length === 0 ? false : true
                )
                ? true : false
        ))
        , [form])

    const handleCreateOrder = useCallback(() => {
        setApiCall(true)
        api.post('/orders', { service_id: service.service_id, order_amount_mult: headsAmount > 1 ? headsAmount : null, lang: lang, order_details: form }).then(({ data }) => {
            api.get('/payments/paymentUrl/' + data.order_id).then(({ data }) => {
                clearForm()
                window.location.href = data.url;
            }).catch(err => {
                console.log(err);
            }).finally(() => setApiCall(false))
        }).catch((err) => {
            console.log(err);
            setApiCall(false)
        })
    }, [service, headsAmount, form, lang])

    const handleDeleteFieldValue = useCallback((stepIndex: number, fieldIndex: number) => {
        if (!setForm) return
        setForm(prevForm => {
            const newSteps = [...prevForm];

            if (!newSteps[stepIndex].fields) newSteps[stepIndex].fields = [];
            const newFields = [...newSteps[stepIndex].fields];

            // If the field exists and has a 'field_value' key, delete it
            if (newFields[fieldIndex]) {
                delete newFields[fieldIndex].field_value;
            }

            newSteps[stepIndex] = {
                ...newSteps[stepIndex],
                fields: newFields,
            };

            return newSteps;
        });
    }, []);

    const clearForm = useCallback(() => {
        form.forEach((s, si) => s.fields.forEach((f, fi) => handleDeleteFieldValue(si, fi)))
    }, [])

    useEffect(() =>
        localStorage.setItem(`service_${service.service_id}`, JSON.stringify({ form: form, version: service.service_version }))
        , [form])



    return (
        <Grid2 container size={'grow'} spacing={openConfirm ? 2 : 0} sx={{ transition: { xs: 'none', lg: openConfirm ? 'all 0.8s cubic-bezier(0, 0.5, 0.2, 1)' : 'all 0.5s cubic-bezier(0.6, 0, 0.8, 0.4)' } }}>
            <Container display={{ xs: openConfirm ? 'none' : 'flex', lg: 'flex' }} size={{ xs: openConfirm ? 0 : 'grow', lg: 'grow' }}>
                <Grid2 size={'grow'} container height={'100%'} padding={2} flexDirection={'column'}>
                    <Grid2 container size={'auto'} padding={2} display={'flex'} alignItems={'center'}>
                        <Grid2>
                            <IconButton onClick={() => navigate('/services')} color="primary" disableRipple>
                                {lang === 'ar' ? <ArrowForwardIos /> : <ArrowBackIos />}
                            </IconButton>
                        </Grid2>
                        <Grid2>
                            <Typography variant='h6' fontWeight={'bold'} color='primary'>
                                {translate("Fill Form")}
                            </Typography>
                        </Grid2>
                    </Grid2>


                    <Grid2 size={'grow'}>
                        <OrderStepper form={form} setForm={setForm} readOnly={openConfirm} />
                    </Grid2>

                    <Grid2 container display={'flex'} justifyContent={'end'} alignItems={'center'} spacing={4}>
                        <Grid2 display={service.is_per_head ? 'flex' : 'none'}>
                            <TextField size='small' type='number' label={translate('Persons')} defaultValue={1} value={headsAmount.toString()} onChange={e => setHeadsAmount(parseInt(e.target.value) > 0 ? parseInt(e.target.value.replace(/^0+/, '')) : 0)} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', width: 70 }} />
                        </Grid2>
                        <Grid2>
                            <Typography sx={{ paddingTop: '2px' }} fontWeight={'bold'}><span style={{ color: theme.palette.secondary.dark }}>{translate("Total Price")}: </span><span style={{ color: headsAmount > 0 ? 'black' : theme.palette.error.main }}>{((service.service_price / 100) * headsAmount).toFixed(2)} {translate("SAR")}</span></Typography>
                        </Grid2>
                        <Grid2>
                            <Button variant='contained' onClick={() => setOpenConfirm(true)} disabled={completedSteps.some(step => !step) || headsAmount < 1 || openConfirm}>
                                {translate("Place Order")}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Container>

            <Container size={openConfirm ? 'grow' : -1} sx={{ transition: { xs: 'none', lg: 'all 0.5s ease' } }} flexGrow={openConfirm ? { xs: 1, lg: 0.5 } : 0}>
                <Grid2 container height={'100%'} display={openConfirm ? 'flex' : 'none'} flexDirection={'column'} padding={2} spacing={2}>

                    <Grid2 container size={'auto'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>

                        <Grid2>
                            <Typography variant='h5'>
                                {translate("Confirm Order")}
                            </Typography>
                        </Grid2>

                        <Grid2>
                            <IconButton onClick={() => setOpenConfirm(false)}>
                                <Close />
                            </IconButton>
                        </Grid2>

                    </Grid2>

                    <Divider flexItem sx={{ backgroundColor: t => t.palette.primary.main }} />

                    <Container container flexDirection={'column'} size={'auto'} padding={2} spacing={2} justifySelf={'center'} overflow={'auto'}>

                        <Grid2 container display={'flex'} justifyContent={'space-between'} wrap='nowrap'>
                            <Grid2>
                                <Typography>{translate("Service")}</Typography>
                            </Grid2>
                            <Grid2>
                                <Typography>{translate("Price")}</Typography>
                            </Grid2>
                        </Grid2>

                        <Divider flexItem />

                        <Grid2 container display={'flex'} justifyContent={'space-between'} wrap='nowrap'>
                            <Grid2>
                                <Typography>{service.service_name[lang]}</Typography>
                            </Grid2>
                            <Grid2>
                                <Typography>{(service.service_price / 100).toFixed(2)} {translate("SAR")}</Typography>
                            </Grid2>
                        </Grid2>

                        <Divider flexItem />

                        <Grid2 container display={'flex'} justifyContent={'space-between'} alignItems={'center'} wrap='nowrap'>
                            <Grid2>
                                <Typography>{translate("Persons")}</Typography>
                            </Grid2>
                            <Grid2>
                                <TextField size='small' type='number' label={translate('Persons')} defaultValue={1} value={headsAmount.toString()} onChange={e => setHeadsAmount(parseInt(e.target.value) > 0 ? parseInt(e.target.value.replace(/^0+/, '')) : 0)} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', width: 70 }} />
                            </Grid2>
                        </Grid2>

                        <Divider flexItem />

                        <Grid2 container display={'flex'} justifyContent={'space-between'} wrap='nowrap'>
                            <Grid2>
                                <Typography fontWeight={'bold'}>{translate("Total Price")}</Typography>
                            </Grid2>
                            <Grid2>
                                <Typography fontWeight={'bold'}>{((service.service_price / 100) * headsAmount).toFixed(2)} {translate("SAR")}</Typography>
                            </Grid2>
                        </Grid2>

                    </Container>

                    <Grid2 size={'grow'} />

                    <Divider flexItem sx={{ backgroundColor: t => t.palette.primary.main }} />

                    <Grid2 size={'auto'} display={'flex'} justifyContent={'end'}>
                        <Button variant='contained' onClick={handleCreateOrder} disabled={completedSteps.some(step => !step) || apiCall || headsAmount < 1} endIcon={apiCall ? <CircularProgress /> : null} >
                            {translate("Proceed to Pay")}
                        </Button>
                    </Grid2>

                </Grid2>


            </Container>
        </Grid2>
    )
}

export default OrderCreate